import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  AlertCircle,
  CheckCircle, 
  Award,
  Clock,
  Users
} from 'lucide-react';

const PaidAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-100 to-green-100 rounded-lg py-12 mb-12">
          <div className="text-center">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 flex items-center justify-center gap-3">
              <Brain className="h-10 w-10 text-blue-600" />
              Get Paid Psychological Assessment by From Industry Experts
            </h1>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-6">
              Want some special treatment with a comprehensive paid psychological assessment? Reach out to TwinBrain & understand your brain to train for the future!
            </p>
            <h2 className="text-xl font-semibold text-gray-800">
              12 Selection Sessions Listed After Tons of Research
            </h2>
            <p className="text-md text-gray-600 mt-2">
              We offer dynamic sessions for every kind of personality needs. Take a look below and select one that suits your needs.
            </p>
          </div>
        </div>

        {/* Important Information */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-blue-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Paid Psychological Assessment</h3>
              <p className="text-blue-800 text-sm leading-relaxed">
                Each paid assessment is done Online, face to face, on phone or video call. The assessor will inform you how long or how much time is expected for you to complete the assessment. The assessor will decide the appropriate questionnaires and questions. If an organisation or an Insurance Firm is paying for the fee, you will be able to submit a booking without any fee selected or paid during booking. We work with almost all the health care insurers.
              </p>
            </div>
          </div>
        </div>

        {/* Assessment Options Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-12">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-r">
                    Assessment Type
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 border-r">
                    Face to Face<br />
                    <span className="text-xs font-normal">(Report Included)</span>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                    Remote Audio or Video Call<br />
                    <span className="text-xs font-normal">(Report Included)</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    One Hour Brief Psychological Assessment for Therapy or for Self-Awareness. Self-Help Resources Provided.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£120</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£100</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    Two Hours' Further/Comprehensive Psychological Assessment for Therapy or for Self-Aware ness. Self-Help Resources Provided.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£240</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£200</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    One Hour Brief Psycho-Legal Assessment.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£120</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£100</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    Dyslexia Assessment Discounted Fees Option.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£490</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£450</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    Autism or ADHD or both Discounted Fees Option.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£390</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£350</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    One Therapist Culture or Faith (or both) based Independent Assessment of one Adult or a Child Psychological Health Needs.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£490</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£450</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    Two Therapists Culture or Faith (or both) based Independent Assessment of one Adult or a Child Psychological Health Needs.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£980</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£900</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    Personal Qualities/Strengths Psychological Profile Assessment. Plus Self-Help/ Improvement Resources for one couple, one child, one adult family member, One team or one organisational member.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£390</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£350</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    Life Plan Profile Assessment of one adult or child. Plus Self-Help/Improvement Resources for one adult. Identifying Golden, Silver and Bronze Dreams. Quick, Mid and Long-Term Wins. Identifying SMART goals/Expected Outcomes. Plus Resources etc. Designing Implementation and Evaluation Targets.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£390</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£350</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    Cognitive/Neuropsychological Assessment.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£850</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£800</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    Learning Disability Assessment.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£850</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£800</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 border-r">
                    Brief 1 hour pregnancy related-occupational psychological risk screening.
                  </td>
                  <td className="px-6 py-4 text-center border-r">
                    <div className="text-lg font-bold text-gray-900 mb-2">£250</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-lg font-bold text-gray-900 mb-2">£200</div>
                    <button onClick={() => navigate("/book-now")} className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors w-24">
                      Book Now
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Why Choose Our Paid Assessments?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Online, face-to-face, phone or video call options',
              'Professional expert analysis and consultation',
              'Comprehensive written reports included',
              'Insurance company partnerships available',
              'Self-help resources and follow-up support',
              'Secure and confidential assessment process'
            ].map((feature, index) => (
              <div key={index} className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-lg p-8 text-center text-white">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Award className="h-8 w-8" />
            <h3 className="text-2xl font-bold">Ready to Get Started?</h3>
          </div>
          <p className="text-lg mb-6 opacity-90">
            Book your comprehensive psychological assessment today and take the first step towards better understanding yourself.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <Clock className="h-5 w-5" />
              Schedule Assessment
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <Users className="h-5 w-5" />
              Contact Expert
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaidAssessmentPage;